import { useState, useRef } from 'react';
import { useAsync } from 'react-use';
import FieldService from 'services/FieldService';
import { getUniqueListBy } from 'utility/snippets';
import { FIELD_TYPES } from 'utility/constants';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';

const EMPTY_OBJECT = {};

const contactOnlyFieldTypes = [FIELD_TYPES.Timezone.type];

const allowedRelatedTypes = ['primary', 'one_to_one'];

export const getFilteredContextFields = (
  automationContextCategories,
  automationContextFields,
  defaultFieldsFilter
) =>
  automationContextCategories.reduce((categoryAcc, category) => {
    const fields = automationContextFields
      .filter((field) => field.category === category.id)
      .filter(defaultFieldsFilter);

    if (fields.length) {
      const option = {
        label: category.name,
        options: fields.map((item) => ({
          label: item.displayName,
          value: item.id,
          name: item.name,
          fieldData: item, // [stuart] because the new selector needs value to be a string
        })),
      };

      return [...categoryAcc, option];
    }

    return categoryAcc;
  }, []);

export const useAutomationGetFieldOptions = ({
  objectId,
  defaultFieldsFilter,
  automationContextFields,
  automationContextCategories,
  fieldToModify,
}) => {
  const clientObject = useSelector(getBusinessClientObject);

  const lastFieldType = useRef(fieldToModify?.fieldType);

  // if the field type changes, we need to recalculate the options when it is or was a timezone field
  const [isTimeZoneField, setIsTimeZoneField] = useState(
    contactOnlyFieldTypes.includes(fieldToModify?.fieldType)
  );

  if (lastFieldType.current !== fieldToModify?.fieldType) {
    setIsTimeZoneField(fieldToModify?.fieldType === FIELD_TYPES.Timezone.type);
    lastFieldType.current = fieldToModify?.fieldType;
  }

  // no primaries on contacts
  const { value } = useAsync(async () => {
    try {
      const option = getFilteredContextFields(
        automationContextCategories,
        automationContextFields,
        defaultFieldsFilter
      );

      const rawRelatedOptions = await FieldService.getModel({
        id: objectId,
      });

      const allRelatedOptions = rawRelatedOptions.relatedObjects
        .filter(
          ({ relationType, relatedObject }) =>
            allowedRelatedTypes.includes(relationType) &&
            (!contactOnlyFieldTypes.includes(fieldToModify?.fieldType) ||
              relatedObject === clientObject.id)
        )
        .map((primary) => ({
          label: primary.objectName,
          value: primary.relatedObject,
          relation: primary,
        }));
      const relatedOptions = getUniqueListBy(allRelatedOptions, 'value').sort(
        (a, b) => a.label.localeCompare(b.label)
      );

      return {
        options: option,
        relatedOptions: relatedOptions,
      };
    } catch (err) {
      return EMPTY_OBJECT;
    }
  }, [objectId, isTimeZoneField, fieldToModify?.fieldType, clientObject.id]);

  const { options = null, relatedOptions = null } = value || {
    options: null,
    relatedOptions: null,
  };
  return { options, relatedOptions };
};
